import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert } from 'lucide-react';

import { ApiClientError } from '@/lib/api-client';

import { QueryFailureNotice } from './QueryFailureNotice';

/**
 * Mutually exclusive async states for audit-critical queries (spec-13 §9).
 *
 * Order of precedence: initial loading → forbidden → failed → empty success →
 * data (+ background refresh indicator). A rejected or unresolved query can
 * never render the empty-success state.
 */
export interface AsyncStateBoundaryProps<TData> {
  /** Query still loading its first response. */
  isLoading: boolean;
  /** Query rejected. */
  isError: boolean;
  /** The rejection, used to distinguish forbidden from other failures. */
  error?: unknown;
  /** Background refetch with data already on screen. */
  isFetching?: boolean;
  /** Loaded data (post-success). */
  data: TData | undefined;
  /** True when a successful response contains zero rows. */
  isEmpty?: (data: TData) => boolean;
  /** Retry the failed query. */
  onRetry?: () => void;
  /** Localized empty-success message (only rendered after success). */
  emptyContent?: ReactNode;
  /** Optional partial-failure warning rendered above children. */
  partialWarning?: ReactNode;
  children: (data: TData) => ReactNode;
}

export const isForbiddenError = (error: unknown): boolean =>
  error instanceof ApiClientError && error.status === 403;

export const requestIdOf = (error: unknown): string | undefined => {
  if (error instanceof ApiClientError) {
    if (typeof error.requestId === 'string' && error.requestId) return error.requestId;
  }
  return undefined;
};

export function AsyncStateBoundary<TData>({
  isLoading,
  isError,
  error,
  isFetching,
  data,
  isEmpty,
  onRetry,
  emptyContent,
  partialWarning,
  children,
}: AsyncStateBoundaryProps<TData>) {
  const { t } = useTranslation(['issues']);

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-efs-border p-4 text-sm text-efs-text-muted"
        role="status"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span>{t('efs.issues.states.loading')}</span>
      </div>
    );
  }

  if (isError && isForbiddenError(error)) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-efs-border p-4 text-sm"
        role="alert"
      >
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-efs-text-muted" aria-hidden />
        <div>
          <p className="font-medium text-efs-text-primary">{t('efs.issues.states.forbidden')}</p>
          <p className="text-efs-text-muted">{t('efs.issues.states.forbidden_hint')}</p>
        </div>
      </div>
    );
  }

  if (isError || data === undefined) {
    return <QueryFailureNotice error={error} onRetry={onRetry} />;
  }

  if (isEmpty?.(data)) {
    return (
      <div className="space-y-2">
        {isFetching ? <BackgroundRefreshNote /> : null}
        {emptyContent ?? (
          <p className="text-sm text-efs-text-muted">{t('efs.issues.states.empty')}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {isFetching ? <BackgroundRefreshNote /> : null}
      {partialWarning}
      {children(data)}
    </div>
  );
}

const BackgroundRefreshNote = () => {
  const { t } = useTranslation(['issues']);
  return (
    <p className="flex items-center gap-1.5 text-xs text-efs-text-muted" role="status">
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      {t('efs.issues.states.refreshing')}
    </p>
  );
};
